import React from 'react';
import { TrendingDown, TrendingUp, AlertTriangle } from 'lucide-react';

/*
interface EpochComparisonSummary {
    epochNumbers: number[];
    rpnChanges: { from: number; to: number; diff: number }[];
    costChanges: { from: number; to: number; diff: number }[];
    totalChanges: number;
}
*/

interface EpochComparisonTableProps {
    comparison: any; // We'll refine this type based on actual API response
    onEpochClick?: (epochNumber: number) => void;
    onDelete?: (epochNumber: number) => void;
}

const EpochComparisonTable: React.FC<EpochComparisonTableProps> = ({ comparison, onEpochClick, onDelete }) => {
    if (!comparison || !comparison.epochs) {
        return <div className="p-4 text-text-secondary">Select epochs to compare</div>;
    }

    // Assuming comparison.epochs is an array of full epoch objects
    const epochs = comparison.epochs;

    return (
        <div className="w-full overflow-x-auto bg-card rounded-lg border border-border">
            <table className="w-full text-left border-collapse">
                <thead>
                    <tr className="bg-muted/50 border-b border-border">
                        <th className="p-4 font-semibold text-text-secondary">Metric</th>
                        {epochs.map((epoch: any, index: number) => (
                            <th
                                key={`col-${epoch.epochNumber}-${index}`}
                                className="p-4 font-semibold text-text-primary cursor-pointer hover:text-primary transition-colors"
                                onClick={() => onEpochClick && onEpochClick(epoch.epochNumber)}
                            >
                                <div className="flex flex-col">
                                    <span className="text-lg">Version {epoch.epochNumber}</span>
                                    <span className="text-xs text-text-secondary font-normal">{new Date(epoch.timestamp).toLocaleDateString()}</span>
                                </div>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr className="border-b border-border hover:bg-muted/20">
                        <td className="p-4 font-medium text-text-secondary">Phase</td>
                        {epochs.map((epoch: any, index: number) => (
                            <td key={`phase-${epoch.epochNumber}-${index}`} className="p-4">
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20">
                                    {epoch.phase || 'Development'}
                                </span>
                            </td>
                        ))}
                    </tr>

                    <tr className="border-b border-border hover:bg-muted/20">
                        <td className="p-4 font-medium text-text-secondary">Total RPN</td>
                        {epochs.map((epoch: any, index: number) => {
                            const prevEpoch = index > 0 ? epochs[index - 1] : null;
                            const diff = prevEpoch ? epoch.totalRPN - prevEpoch.totalRPN : 0;

                            return (
                                <td key={`rpn-${epoch.epochNumber}-${index}`} className="p-4">
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold">{epoch.totalRPN}</span>
                                        {index > 0 && (
                                            <span className={`text-xs flex items-center ${diff < 0 ? 'text-green-500' : diff > 0 ? 'text-red-500' : 'text-text-secondary'}`}>
                                                {diff < 0 ? <TrendingDown size={14} /> : diff > 0 ? <TrendingUp size={14} /> : null}
                                                {Math.abs(diff)}
                                            </span>
                                        )}
                                    </div>
                                </td>
                            );
                        })}
                    </tr>

                    <tr className="border-b border-border hover:bg-muted/20">
                        <td className="p-4 font-medium text-text-secondary">Total Cost</td>
                        {epochs.map((epoch: any, index: number) => {
                            const prevEpoch = index > 0 ? epochs[index - 1] : null;
                            const diff = prevEpoch ? epoch.totalCost - prevEpoch.totalCost : 0;

                            return (
                                <td key={`cost-${epoch.epochNumber}-${index}`} className="p-4">
                                    <div className="flex items-center gap-2">
                                        <span className="font-mono">${epoch.totalCost?.toFixed(2)}</span>
                                        {index > 0 && (
                                            <span className={`text-xs flex items-center ${diff < 0 ? 'text-green-500' : diff > 0 ? 'text-red-500' : 'text-text-secondary'}`}>
                                                {diff > 0 ? <TrendingUp size={14} /> : diff < 0 ? <TrendingDown size={14} /> : null}
                                                ${Math.abs(diff).toFixed(2)}
                                            </span>
                                        )}
                                    </div>
                                </td>
                            );
                        })}
                    </tr>

                    <tr className="border-b border-border hover:bg-muted/20">
                        <td className="p-4 font-medium text-text-secondary">Critical Risks</td>
                        {epochs.map((epoch: any, index: number) => (
                            <td key={`risk-${epoch.epochNumber}-${index}`} className="p-4">
                                {epoch.criticalItemCount > 0 ? (
                                    <div className="flex items-center gap-2 text-red-500">
                                        <AlertTriangle size={16} />
                                        <span className="font-semibold">{epoch.criticalItemCount}</span>
                                    </div>
                                ) : (
                                    <span className="text-green-500 text-sm">None</span>
                                )}
                            </td>
                        ))}
                    </tr>

                    <tr className="hover:bg-muted/20">
                        <td className="p-4 font-medium text-text-secondary">Changes</td>
                        {epochs.map((epoch: any, index: number) => (
                            <td key={`changes-${epoch.epochNumber}-${index}`} className="p-4">
                                <div className="text-sm text-text-secondary">
                                    {epoch.changes ? epoch.changes.length : 0} modifications
                                </div>
                            </td>
                        ))}
                    </tr>

                    {/* Actions Row */}
                    <tr className="hover:bg-muted/20 border-t border-border">
                        <td className="p-4 font-medium text-text-secondary">Actions</td>
                        {epochs.map((epoch: any, index: number) => (
                            <td key={`action-${epoch.epochNumber}-${index}`} className="p-4">
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (window.confirm(`Are you sure you want to delete Epoch ${epoch.epochNumber}? This cannot be undone.`)) {
                                            onDelete?.(epoch.epochNumber);
                                        }
                                    }}
                                    className="px-3 py-1 bg-red-500/10 text-red-500 hover:bg-red-500/20 rounded text-xs border border-red-500/20"
                                >
                                    Delete Epoch
                                </button>
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default EpochComparisonTable;
